import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Clock3, GripHorizontal, X } from "lucide-react";
import { doc, onSnapshot, serverTimestamp, updateDoc, writeBatch } from "firebase/firestore";
import { db } from "../firebase";
import { ACCESS_NO_END, employeeAccessUpdate } from "../utils/assistanceAccess";

const accessModes = [
  { id: "dayStart", label: "Från dagens början", detail: "På dagar med publicerat pass kan personalen se schemat från 00:00, även om passet börjar senare." },
  { id: "shiftTime", label: "Under passet", detail: "Åtkomst startar när passet börjar och stängs när passet slutar." },
];

export default function AssistanceScheduleAccess({ companyUid, personnel = [], personnelLoading = false, disabled }) {
  const [timeZone, setTimeZone] = useState("Europe/Stockholm");
  const [mode, setMode] = useState("shiftTime");
  const [popup, setPopup] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);
  const button = useRef(null);
  const panel = useRef(null);
  const busy = useRef(false);

  useEffect(() => {
    if (!companyUid) return undefined;
    return onSnapshot(doc(db, "users", companyUid), (snapshot) => {
      const policy = snapshot.data()?.smartAssistanceAccessPolicy;
      const savedTimeZone = policy?.timeZone;
      if (savedTimeZone) setTimeZone(savedTimeZone);
      if (accessModes.some((item) => item.id === policy?.mode)) setMode(policy.mode);
    }, () => setError("Kunde inte hämta åtkomstregeln."));
  }, [companyUid]);

  const close = () => { if (!busy.current) { setPopup(null); button.current?.focus(); } };
  useEffect(() => {
    if (!popup) return undefined;
    const dismiss = (event) => {
      if (event.type === "keydown" ? event.key === "Escape" : !panel.current?.contains(event.target) && !button.current?.contains(event.target)) {
        if (!busy.current) { setPopup(null); if (event.type === "keydown") button.current?.focus(); }
      }
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("keydown", dismiss);
    return () => { document.removeEventListener("pointerdown", dismiss); document.removeEventListener("keydown", dismiss); };
  }, [popup]);

  const open = () => {
    if (popup) { close(); return; }
    setError("");
    const rect = button.current.getBoundingClientRect();
    const width = Math.min(720, window.innerWidth - 24);
    setPopup({ left: Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12)), top: Math.max(12, Math.min(rect.bottom + 8, window.innerHeight - 380)) });
  };
  const startDrag = (event) => {
    if (event.target.closest("button") || event.button !== 0) return;
    const heading = event.currentTarget;
    const rect = panel.current.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    heading.setPointerCapture(event.pointerId);
    heading.onpointermove = (move) => setPopup({
      left: Math.max(12, Math.min(move.clientX - offsetX, window.innerWidth - rect.width - 12)),
      top: Math.max(12, Math.min(move.clientY - offsetY, window.innerHeight - rect.height - 12)),
    });
    heading.onpointerup = heading.onpointercancel = () => { heading.onpointermove = null; };
  };
  const save = async (event) => {
    event.preventDefault();
    if (busy.current || !companyUid || disabled) return;
    busy.current = true; setSaving(true); setError("");
    try {
      await updateDoc(doc(db, "users", companyUid), {
        smartAssistanceAccessPolicy: {
          mode,
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || timeZone || "Europe/Stockholm",
          updatedAt: serverTimestamp(),
        },
      });
      setPopup(null); button.current?.focus();
    } catch { setError("Kunde inte spara åtkomstregeln. Försök igen."); }
    finally { busy.current = false; setSaving(false); }
  };

  const setAllAccess = async (enabled) => {
    if (!companyUid || disabled || personnelLoading || busy.current || personnel.length === 0) return;
    busy.current = true; setBulkSaving(true); setError(""); setMessage("");
    try {
      const now = new Date().toISOString();
      for (let start = 0; start < personnel.length; start += 450) {
        const batch = writeBatch(db);
        if (start === 0) batch.update(doc(db, "users", companyUid), {
          smartAssistanceAccessPolicy: {
            mode: "manual",
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || timeZone || "Europe/Stockholm",
            updatedAt: serverTimestamp(),
          },
        });
        personnel.slice(start, start + 450).forEach((employee) => {
          batch.update(doc(db, "users", companyUid, "employed", employee.id), employeeAccessUpdate(employee, {
            enabled,
            opensAt: enabled ? now : null,
            closesAt: enabled ? ACCESS_NO_END : null,
            mode: "permanent",
          }, true));
        });
        await batch.commit();
      }
      setMessage(enabled ? "Åtkomst permanent aktiverad för all personal." : "Åtkomst permanent avaktiverad för all personal.");
    } catch (saveError) {
      console.error("Kunde inte ändra åtkomst för all personal:", saveError);
      setError("Kunde inte ändra åtkomst för all personal. Försök igen.");
    } finally { busy.current = false; setBulkSaving(false); }
  };

  return <>
    <button ref={button} type="button" className="as-icon as-access-clock" title="Automatisk åtkomst till appen" aria-label="Automatisk åtkomst till appen" aria-expanded={Boolean(popup)} aria-haspopup="dialog" disabled={disabled || !companyUid} onClick={open}><Clock3 size={19} aria-hidden="true" /></button>
    {popup && createPortal(<form ref={panel} role="dialog" aria-modal="false" aria-label="Automatisk åtkomst i appen" className="as-access-popup as-access-policy-popup" style={popup} onSubmit={save}>
      <div className="as-access-heading" onPointerDown={startDrag}><strong><GripHorizontal size={16} aria-hidden="true" /> Åtkomst i appen</strong><button type="button" aria-label="Stäng åtkomstinställningar" disabled={saving} onClick={close}><X size={16} /></button></div>
      <p className="as-access-intro">Välj hur appåtkomsten ska följa personalens publicerade pass från PopupShift.</p>
      <div className="as-access-policy-options" role="radiogroup" aria-label="Automatisk passåtkomst">
        {accessModes.map((item) => <button key={item.id} type="button" role="radio" aria-checked={mode === item.id} aria-pressed={mode === item.id} disabled={saving || bulkSaving} onClick={() => setMode(item.id)}>
          <strong>{item.label}</strong><span>{item.detail}</span>
        </button>)}
      </div>
      <div className="as-access-bulk-actions" aria-label="Ändra åtkomst för all personal">
        <strong>All personal</strong>
        <button type="button" disabled={disabled || personnelLoading || bulkSaving || personnel.length === 0} onClick={() => setAllAccess(true)}>{bulkSaving ? "Sparar…" : "Aktivera permanent"}</button>
        <button type="button" disabled={disabled || personnelLoading || bulkSaving || personnel.length === 0} onClick={() => setAllAccess(false)}>{bulkSaving ? "Sparar…" : "Avaktivera permanent"}</button>
        {!personnelLoading && personnel.length === 0 && <span>Ingen personal att ändra.</span>}
      </div>
      <p className="as-access-note"><Clock3 size={14} aria-hidden="true" /> Automatiska lägen använder företagets tidszon. Nattpass fortsätter till sluttiden nästa dag. Permanenta val gäller tills ett automatiskt läge sparas.</p>
      {message && <p role="status">{message}</p>}
      {error && <p role="alert">{error}</p>}
      <div className="as-access-buttons"><button type="button" disabled={saving} onClick={close}>Avbryt</button><button type="submit" disabled={saving || disabled}>{saving ? "Sparar…" : "Spara"}</button></div>
    </form>, document.body)}
  </>;
}
